import { Decimal, money, type MoneyInput } from './money';

/**
 * How a user wants their balance shown. The money itself always stays in the
 * Deriv account currency (USD — the only one Options accounts support); BRL is
 * a display-only conversion at the current USD/BRL rate.
 */
export const BALANCE_CURRENCIES = ['USD', 'BRL'] as const;
export type BalanceCurrency = (typeof BALANCE_CURRENCIES)[number];
export const DEFAULT_BALANCE_CURRENCY: BalanceCurrency = 'USD';

export type FxSource = 'awesomeapi' | 'bcb' | 'sim';

export interface FxQuote {
  pair: 'USD/BRL';
  /** BRL paid for 1 USD (buy side), decimal string. */
  rate: string;
  source: FxSource;
  /** When the source priced it (ISO). */
  quotedAt: string;
  /** When we fetched it (ISO). */
  fetchedAt: string;
}

/** Rates outside this band are treated as a broken feed, never shown. */
export const USD_BRL_SANE_RANGE = { min: 2, max: 15 } as const;

export function isSaneUsdBrl(rate: MoneyInput): boolean {
  const r = money(rate);
  return r.gte(USD_BRL_SANE_RANGE.min) && r.lte(USD_BRL_SANE_RANGE.max);
}

export function balanceCurrencyOf(preferences: unknown): BalanceCurrency {
  const v = (preferences as { balanceCurrency?: unknown } | null | undefined)?.balanceCurrency;
  return v === 'BRL' ? 'BRL' : DEFAULT_BALANCE_CURRENCY;
}

/**
 * Smallest entry Deriv accepts, per account currency (Options accounts are USD only): US$ 0,35
 * ("Please enter a stake amount that's at least 0.35", read live 2026-10-27). A quote's own
 * figure (validation_params.stake.min) wins when there is one.
 */
export const BROKER_MIN_STAKE: Readonly<Record<string, string>> = { USD: '0.35' };

/** The minimum entry in `currency` (an account currency); unknown currencies take the dollar one. */
export function minStakeFor(currency: string | null | undefined): string {
  return (currency && BROKER_MIN_STAKE[currency]) || MIN_STAKE_USD;
}

/** Smallest stake of a dollar account. */
export const MIN_STAKE_USD = '0.35';

/**
 * A USD amount in BRL, to the nearest cent. The same rounding for balances
 * and stakes, so equal dollar amounts always read as equal reais.
 */
export function usdToBrl(usd: MoneyInput, rate: MoneyInput): Decimal {
  return money(usd).times(money(rate)).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
}

/**
 * A BRL amount typed by the user, in USD. Stakes go to Deriv in cents (2); limits
 * such as a session meta keep 4 decimals so R$ 50 still reads R$ 50,00 later.
 */
export function brlToUsd(brl: MoneyInput, rate: MoneyInput, decimals = 2): Decimal {
  return money(brl).div(money(rate)).toDecimalPlaces(decimals, Decimal.ROUND_HALF_UP);
}

/** Lowest BRL amount whose conversion is still worth at least `usd` (e.g. the minimum stake). */
export function minBrlFor(usd: MoneyInput, rate: MoneyInput): Decimal {
  return money(usd).times(money(rate)).toDecimalPlaces(2, Decimal.ROUND_UP);
}
